import { ReactElement, ReactNode } from 'react'
import { Box, Tooltip, useTheme } from '@mui/material'
import { MaterialSymbol } from '@ui2/icons'

export type SortButtonProps = {
  tooltip: string
  active: boolean
  onClick: () => void
  /** Shown as an arrow after the icon; leave out for options without a direction. */
  direction?: 'asc' | 'desc'
  disabled?: boolean
  /** The sort option's icon. */
  children: ReactNode
  'data-cy'?: string
}

/** One sort option of a `SortButtonGroup`, highlighted while active. */
const SortButton = ({
  tooltip,
  active,
  onClick,
  direction,
  disabled = false,
  children,
  'data-cy': dataCy,
}: SortButtonProps): ReactElement => {
  const theme = useTheme()
  const color = disabled
    ? theme.palette.text.disabled
    : active
    ? theme.palette.primary.main
    : theme.palette.text.secondary
  return (
    <Tooltip title={tooltip} arrow disableInteractive>
      {/* The span keeps the tooltip working while the button is disabled. */}
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          '& + &': { borderLeft: `1px solid ${theme.palette.divider}` },
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={onClick}
          disabled={disabled}
          aria-label={tooltip}
          data-cy={dataCy}
          sx={{
            border: 0,
            px: 1,
            cursor: disabled ? 'default' : 'pointer',
            background: active && !disabled ? 'rgba(55,132,52,0.10)' : theme.palette.common.white,
            color,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.25,
            minWidth: 30,
          }}
        >
          {children}
          {direction && (
            <MaterialSymbol
              icon={direction === 'asc' ? 'arrow_upward' : 'arrow_downward'}
              size={12}
            />
          )}
        </Box>
      </Box>
    </Tooltip>
  )
}

export default SortButton
